
import { faker } from '@faker-js/faker';
import { generateOrderNumber } from '@/utils/orderNumberGenerator';
import { CustomerContact, CustomerMessage } from '@/types/resellerCustomer';

// Define CRM customer types
export interface CRMCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: Date;
  lastContact?: Date;
  notes?: string;
  tags: string[];
  orderHistory: CRMOrder[];
}

export interface CRMOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  amount: number;
  date: Date;
  status: 'completed' | 'pending' | 'cancelled';
  items: string[];
}

// Generate mock CRM data
export const generateMockCRMCustomers = (count: number = 20): CRMCustomer[] => {
  return Array.from({ length: count }, () => {
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    const ordersCount = faker.number.int({ min: 0, max: 8 });
    
    return {
      id: faker.string.uuid(),
      name: `${firstName} ${lastName}`,
      email: faker.internet.email({ firstName, lastName }),
      phone: faker.phone.number(),
      company: faker.company.name(),
      status: faker.helpers.arrayElement(['active', 'inactive', 'pending']),
      createdAt: faker.date.past(),
      lastContact: faker.helpers.maybe(() => faker.date.recent(), { probability: 0.7 }),
      notes: faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.5 }),
      tags: faker.helpers.multiple(() => faker.word.sample(), {
        count: { min: 0, max: 4 },
      }),
      orderHistory: generateMockCRMOrders(ordersCount, faker.string.uuid())
    };
  });
};

export const generateMockCRMOrders = (count: number, customerId: string): CRMOrder[] => {
  return Array.from({ length: count }, () => {
    const orderPrefix = faker.helpers.arrayElement(['RO', 'RSO']) as 'RO' | 'RSO';
    return {
      id: faker.string.uuid(),
      orderNumber: generateOrderNumber(orderPrefix),
      customerId,
      amount: parseFloat(faker.finance.amount({ min: 50, max: 5000 })),
      date: faker.date.recent({ days: 90 }),
      status: faker.helpers.arrayElement(['completed', 'pending', 'cancelled']),
      items: faker.helpers.multiple(() => faker.commerce.productName(), {
        count: { min: 1, max: 5 },
      })
    };
  });
};

// Generate sample contacts and messages for ResellerCRM that match the CustomerContact interface in resellerCustomer.ts
export const generateSampleContacts = (count: number = 30): CustomerContact[] => {
  return Array.from({ length: count }, () => {
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    
    return {
      id: faker.string.uuid(),
      name: `${firstName} ${lastName}`,
      email: faker.internet.email({ firstName, lastName }),
      phone: faker.phone.number(),
      company: faker.company.name(),
      position: faker.person.jobTitle(),
      orderIds: faker.helpers.multiple(() => faker.string.uuid(), { count: { min: 0, max: 3 }}),
      status: faker.helpers.arrayElement(['active', 'inactive', 'pending']),
      notes: faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.5 }),
      lastContactedAt: faker.helpers.maybe(() => faker.date.recent(), { probability: 0.7 }),
      createdAt: faker.date.past(),
      address: {
        street: faker.location.streetAddress(),
        city: faker.location.city(),
        state: faker.location.state(),
        zip: faker.location.zipCode(),
        country: faker.location.country()
      },
      tags: faker.helpers.multiple(() => faker.word.sample(), {
        count: { min: 0, max: 4 },
      }),
    };
  });
};

export const generateSampleMessages = (contacts: CustomerContact[]): CustomerMessage[] => {
  const messages: CustomerMessage[] = [];
  
  contacts.forEach(contact => {
    const messageCount = faker.number.int({ min: 0, max: 5 });
    
    for (let i = 0; i < messageCount; i++) {
      const messageType = faker.helpers.arrayElement(['email', 'sms', 'note']) as 'email' | 'sms' | 'note';
      const direction = faker.helpers.arrayElement(['inbound', 'outbound']) as 'inbound' | 'outbound';
      
      messages.push({
        id: faker.string.uuid(),
        customerId: contact.id,
        type: messageType,
        direction: direction,
        content: faker.lorem.paragraph(),
        subject: messageType === 'email' ? faker.lorem.sentence() : undefined,
        sentAt: faker.date.recent({ days: 30 }),
        readAt: faker.helpers.maybe(() => faker.date.recent(), { probability: 0.5 }),
        attachments: faker.helpers.maybe(() => [{
          name: `${faker.system.fileName()}.pdf`,
          url: faker.internet.url(),
          type: 'application/pdf',
          size: faker.number.int({ min: 10000, max: 5000000 })
        }], { probability: 0.2 }),
        status: faker.helpers.arrayElement(['sent', 'delivered', 'failed', 'draft']),
      });
    }
  });
  
  return messages;
};

// Export some sample data
export const sampleCRMCustomers = generateMockCRMCustomers(50);

export const getCustomerById = (id: string): CRMCustomer | undefined => {
  return sampleCRMCustomers.find(customer => customer.id === id);
};
